import { useEffect, useState } from "react";
import { Box, Typography, Divider, Skeleton, Chip } from "@mui/joy";
import { DocumentPreviewWidget } from "../../../../../../components";
import { useDocument, useProject as useProjectQuery } from "../../../../../../hooks/query";
import { useProject, useNotification } from "../../../../../../hooks";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion.create(Box);

export default function Documentation() {
    const { project } = useProject();
    const projectId = project?.id;

    const { useList } = useDocument();
    const { useGetProjectById, useGetEagleViewReport } = useProjectQuery();
    const { showError } = useNotification();

    const listSvc = useList();
    const projectSvc = useGetProjectById(projectId!);
    const getEvReportSvc = useGetEagleViewReport();

    const [documents, setDocuments] = useState<any[]>([]);

    const fetchDocuments = async () => {
        if (!projectId) return;
        try {
            const data = await listSvc.mutateAsync({ ProjectId: projectId });
            setDocuments(data || []);
        } catch (e) {
            console.error("Failed to fetch documents", e);
        }
    };

    const handleDownloadEagleView = async (reportId: string) => {
        try {
            const response = await getEvReportSvc.mutateAsync({ ReportId: reportId });
            const downloadUrl = response?.ReportDownloadLink;
            if (downloadUrl) {
                window.open(downloadUrl, "_blank");
            } else if (typeof response === "string" && response.startsWith("http")) {
                window.open(response, "_blank");
            } else {
                showError("EagleView report link not found.");
            }
        } catch (e: any) {
            showError(e.message || "Failed to load EagleView report. Please try again.");
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [projectId]);

    if (!project) return null;

    const projectDetails = projectSvc.data;

    const getDocCategory = (name: string) => {
        const n = String(name).toUpperCase();
        if (n.includes("CONTRACT") || n.includes("SIGNOFF") || n.includes("WAIVER")) return "Core";
        if (n.includes("ORDER") || n.includes("EAGLEVIEW")) return "Orders";
        if (n.includes("INVOICE")) return "Finances";
        return "Other";
    };

    const formattedDocs: any[] = documents.map((doc: any) => ({
        title: String(doc.Name).replace(/_/g, " "),
        description: `Project Document`,
        fileType: "PDF",
        status: doc.Status,
        id: doc.Id,
        category: getDocCategory(doc.Name),
    }));

    if (projectDetails?.EagleViewOrderId) {
        formattedDocs.unshift({
            title: "EagleView Report",
            description: `Order ID: ${projectDetails.EagleViewOrderId}`,
            fileType: "PDF",
            status: "AVAILABLE",
            id: "eagleview",
            onDownload: () => handleDownloadEagleView(projectDetails.EagleViewOrderId!),
            loading: getEvReportSvc.isPending,
            category: "Orders",
        });
    }

    const categories = ["Core", "Orders", "Finances"];
    const isLoading = listSvc.isPending || projectSvc.isPending;

    return (
        <Box px={3} py={3} pt={2}>
            <AnimatePresence mode="wait">
                {isLoading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {[...Array(2)].map((_, i) => (
                            <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <Box sx={{ borderLeft: "4px solid", borderColor: "primary.200", pl: 2, py: 0.5 }}>
                                    <Skeleton variant="text" width={200} level="h4" />
                                    <Skeleton variant="text" width={320} level="body-sm" />
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={5}>
                                    <DocumentationSkeleton count={3} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <MotionBox
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        display="flex"
                        flexDirection="column"
                        gap={3}
                    >
                        {categories.map((cat, i) => {
                            const filteredDocs = formattedDocs.filter((d) => d.category === cat);
                            if (filteredDocs.length === 0) return null;

                            return (
                                <Box key={cat} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Box
                                        sx={{
                                            borderLeft: "4px solid",
                                            borderColor: "primary.500",
                                            pl: 2,
                                            py: 0.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Typography level="h4">{cat}</Typography>
                                            <Chip variant="soft" color="primary" size="sm">
                                                {filteredDocs.length} {filteredDocs.length === 1 ? "File" : "Files"}
                                            </Chip>
                                        </Box>
                                        <Typography level="body-sm" color="neutral">
                                            Essential {cat.toLowerCase()} documentation and asset management.
                                        </Typography>
                                    </Box>

                                    <Box display="flex" flexWrap="wrap" gap={5}>
                                        {filteredDocs.map((doc, index) => (
                                            <DocumentPreviewWidget
                                                key={doc.id || index}
                                                title={doc.title}
                                                description={doc.description}
                                                fileType={doc.fileType}
                                                status={doc.status}
                                                onButtonClick={doc.onDownload}
                                                loading={doc.loading}
                                            />
                                        ))}
                                    </Box>
                                    {i <
                                        categories.filter((c) => formattedDocs.some((d) => d.category === c)).length -
                                            1 && <Divider sx={{ mt: 1 }} />}
                                </Box>
                            );
                        })}
                        {formattedDocs.length === 0 && (
                            <Box
                                sx={{
                                    width: "100%",
                                    py: 8,
                                    textAlign: "center",
                                    bgcolor: "background.level1",
                                    borderRadius: "xl",
                                    border: "1px dashed",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography level="h4" color="neutral">
                                    No Documents Yet
                                </Typography>
                                <Typography level="body-sm" sx={{ opacity: 0.6 }}>
                                    No documentation has been uploaded for this project yet.
                                </Typography>
                            </Box>
                        )}
                    </MotionBox>
                )}
            </AnimatePresence>
        </Box>
    );
}

const DocumentationSkeleton = ({ count = 4 }: { count?: number }) => {
    return (
        <>
            {[...Array(count)].map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        flex: "1 1 230px",
                        maxWidth: 300,
                        height: 280,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                    }}
                >
                    {/* Rotated Background Layer Skeleton */}
                    <Box
                        sx={{
                            width: "90%",
                            height: 250,
                            border: "1px solid",
                            borderColor: "neutral.outlinedBorder",
                            borderRadius: "lg",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: 0,
                            bgcolor: "background.level1",
                            transform: "rotate(-2deg)",
                            opacity: 0.5,
                        }}
                    />

                    {/* Main Skeleton Card */}
                    <Box
                        sx={{
                            width: "90%",
                            height: 250,
                            borderRadius: "lg",
                            p: 0,
                            zIndex: 1,
                            position: "relative",
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "neutral.outlinedBorder",
                            bgcolor: "background.surface",
                        }}
                    >
                        <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
                            <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: "6px" }} />
                        </Box>

                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height="100%"
                                sx={{ position: "absolute", top: 0, left: 0, zIndex: -1, opacity: 0.1 }}
                            />
                        </Box>

                        <Box sx={{ p: 1, pl: 1.5, bgcolor: "background.surface", mt: "auto" }}>
                            <Divider inset="context" sx={{ mb: 1 }} />
                            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" width="60%" height={14} sx={{ mb: 1.5 }} />

                            <Divider inset="context" sx={{ mb: 1 }} />
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: "md" }} />
                                <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: "md" }} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ))}
        </>
    );
};
